import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const CUSTOMER_JOURNEY_EXAMPLES = 'https://cdn.magicpatterns.com/uploads/j9MzYRPzwKpSyc6y7h75ar/Screenshot_2026-09-11_at_11.48.05.png';
interface Hotspot {
  label: string;
  url: string;
  /** Position as a percentage of the collage image itself. */
  left: string;
  top: string;
  width: string;
  height: string;
}
const HOTSPOTS: Hotspot[] = [{
  label: 'Interactive data Snak',
  url: 'https://player.snakker.io/review/Vec1RTCYxoyGHuTgbj5x',
  left: '16.6%',
  top: '15.5%',
  width: '6.8%',
  height: '21%'
}, {
  label: 'Heart attack diagnosis Snak',
  url: 'https://player.snakker.io/s/PjNDMqQ27Y',
  left: '2.4%',
  top: '42%',
  width: '9%',
  height: '15%'
}, {
  label: 'Snak results report',
  url: 'https://editor.snakker.io/snaks/d7337943-dff1-4a60-bd78-2038bbde8d4a/results',
  left: '11.5%',
  top: '37.5%',
  width: '3.4%',
  height: '6%'
}, {
  label: 'Clinical treasure chest Snak',
  url: 'https://content.snakker.io/snak/78Gic6GKtzqS6f3nWeH0JwurO5MiTUL8/index.html',
  left: '2.4%',
  top: '58.5%',
  width: '9%',
  height: '21%'
}, {
  label: 'Risdiplam patient story Snak',
  url: 'https://player.snakker.io/s/PoLixnZjCJ',
  left: '63.2%',
  top: '43.5%',
  width: '14.5%',
  height: '15%'
}, {
  label: 'Bonding Snak',
  url: 'https://bonding.snakker.io',
  left: '74.2%',
  top: '70.5%',
  width: '6.8%',
  height: '18%'
}, {
  label: 'Risdiplam Snak results report',
  url: 'https://editor.snakker.io/snaks/9fd586df-9395-4715-95bd-2f0f7c49877c/results',
  left: '77.2%',
  top: '34.5%',
  width: '3.4%',
  height: '6.5%'
}, {
  label: 'Roche Snak',
  url: 'https://player.snakker.io/review/IXR5v_BxPpLovsuMiFjv',
  left: '84%',
  top: '66%',
  width: '12%',
  height: '24%'
}];
export function SlideCustomerJourneyExamples({}: SlideProps) {
  const [active, setActive] = useState<Hotspot | null>(null);
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActive(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active]);
  return <InteriorSlide surface data-id="element-248" data-mp="3sml0ru69tnis">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.8vw,1.75rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-249" data-mp="3slt4si8rhnrz">
        <div data-id="element-250" data-mp="692y8464yyv3">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-251" data-mp="68uogl5b89o4">
            Experience portfolio
          </p>
          <h2 className="text-[clamp(2rem,3.4vw,3.3rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-252" data-mp="f87uf3u86g43">
            Snaks throughout the{' '}
            <span className="text-[var(--brand)]" data-id="element-253" data-mp="1vnvv3m4xvxl9">customer journey</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-254" data-mp="1qo95sebu6qw3">
          Real examples spanning awareness, education, interaction, engagement
          and insight generation. Click a highlighted example to run it live.
        </p>
      </motion.header>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.18,
      duration: 0.65
    }} className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-3xl border border-[var(--brand)]/10 bg-white p-3 shadow-sm" data-id="element-255" data-mp="2ee3jsz7w6lhx">
        <div className="relative h-full" data-id="element-256" data-mp="324heyylrx0gq">
          <img src={CUSTOMER_JOURNEY_EXAMPLES} alt="Examples of Snak experiences used throughout the customer journey" className="h-full w-auto object-contain" data-id="element-257" data-mp="326ajo8cttam3" />

          {HOTSPOTS.map(hotspot => <button key={hotspot.url} type="button" onClick={() => setActive(hotspot)} aria-label={`Run the ${hotspot.label} live`} style={{
          left: hotspot.left,
          top: hotspot.top,
          width: hotspot.width,
          height: hotspot.height
        }} className="absolute rounded-lg ring-2 ring-[var(--brand)]/0 transition hover:scale-[1.04] hover:bg-[var(--brand)]/10 hover:ring-[var(--brand)]/70 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--brand)]/60" data-id="element-258" data-mp="2lxbfdmmxty53" />)}
        </div>
      </motion.div>

      <AnimatePresence data-id="element-259" data-mp="3jl3cyhpwr1d7">
        {active && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex flex-col bg-[var(--fg)]/80 p-[clamp(1rem,3vw,3rem)]" role="dialog" aria-modal="true" aria-label={active.label} data-id="element-260" data-mp="3jj8t76qdhfap">
            <div className="mb-3 flex flex-shrink-0 items-center justify-between gap-4" data-id="element-261" data-mp="1lntlvw0c54yt">
              <p className="truncate text-lg font-extrabold text-white" data-id="element-262" data-mp="1hn1bgzfq8u0y">
                {active.label}
              </p>
              <div className="flex flex-shrink-0 items-center gap-2" data-id="element-263" data-mp="tyi54pg8mrbi">
                <a href={active.url} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white/15 px-4 py-2 text-xs font-bold text-white transition hover:bg-white/25" data-id="element-264" data-mp="ozdtzt9r6jij">
                  Open in new tab
                </a>
                <button type="button" onClick={() => setActive(null)} aria-label="Close the live Snak" className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[var(--fg)] transition hover:bg-[var(--brand)] hover:text-white" data-id="element-265" data-mp="2ec90wijgvna5">
                  <XIcon className="h-4 w-4" data-id="element-266" data-mp="1c1ytmw0pwipy" />
                </button>
              </div>
            </div>

            <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.25
        }} className="min-h-0 flex-1 overflow-hidden rounded-2xl bg-white shadow-2xl" data-id="element-267" data-mp="1l0u0897obuts">
              <iframe key={active.url} src={active.url} title={active.label} className="h-full w-full border-0" allow="fullscreen" data-id="element-268" data-mp="sa2b1vp106fy" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </InteriorSlide>;
}